import {
  Action,
  AIONZ_DIALOG_DATA,
  AionzDialogModule,
  AionzDialogRef,
  AionzTooltip,
  CardTableModule,
  ConfirmationModalService,
  HeaderBottomSheetComponent,
  ModalTypes,
} from '@aionzdev/design-system';
import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  MergeComparison,
  MergeItem,
  MergePurchaseRequestsDialogData
} from '../../../interfaces/merge-purchase-requests-dialog-data';
import { SendPurchaseRequestForQuoteItem } from '../../../interfaces/send-purchase-request-for-quote-dialog-data';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { PurchaseSupplier, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { ItemSuppliersSummary } from '../../../interfaces/item-suppliers-summary';
import { buildItemSuppliersSummary } from '../../atoms/item-suppliers-tooltip/build-item-suppliers-summary';
import { ItemSuppliersTooltipComponent } from '../../atoms/item-suppliers-tooltip/item-suppliers-tooltip.component';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { branchLabel } from '../../utils/branch-label.util';

@Component({
  selector: 'aionz-merge-purchase-requests-dialog',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    MatChipsModule,
    MatIconModule,
    CardTableModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatCheckboxModule,
    AionzDialogModule,
    HeaderBottomSheetComponent,
    PurchaseRequestStatusComponent,
    AionzTooltip
  ],
  templateUrl: './merge-purchase-requests-dialog.component.html',
  styleUrl: './merge-purchase-requests-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MergePurchaseRequestsDialogComponent implements OnInit {
  public data: MergePurchaseRequestsDialogData = inject(AIONZ_DIALOG_DATA);
  public _dialogService = inject(ConfirmationModalService);
  public dialogRef = inject(AionzDialogRef<MergePurchaseRequestsDialogComponent>);
  readonly sort = viewChild.required(MatSort);
  protected dataSource = signal(new MatTableDataSource<MergeItem>([]));
  protected selection = new SelectionModel<MergeItem>(true, []);
  /** Outras solicitações de onde vêm os itens repetidos (sem a atual). */
  protected readonly otherPurchaseIds: string[] = Array.from(
    new Set(this.data.items.map(item => item.purchaseId).filter(id => !!id && id !== this.data.id))
  );

  /** Com itens de mais de uma solicitação, a tabela ganha a coluna "Solicitação". */
  protected readonly showPurchaseColumn = this.otherPurchaseIds.length > 1;

  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly destroyRef = inject(DestroyRef);

  /** Título das outras solicitações, buscado ao abrir (a API de duplicados só traz o número). */
  protected readonly otherTitles = signal<Record<string, string>>({});
  /** Filial de cada item nas outras solicitações, por orderId. `undefined` = ainda carregando. */
  private readonly otherBranches = signal<Record<string, string> | undefined>(undefined);

  protected readonly contextWhere = computed(() => {
    const titles = this.otherTitles();
    const label = (id: string) => (titles[id] ? `#${id} — ${titles[id]}` : `#${id}`);
    const ids = this.otherPurchaseIds;
    if (ids.length === 0) return 'em outra solicitação';
    if (ids.length === 1) return `com a solicitação ${label(ids[0])}`;
    return `com as solicitações ${ids.slice(0, -1).map(label).join(', ')} e ${label(ids[ids.length - 1])}`;
  });

  /** Produtos distintos em comum (um produto pode vir em mais de uma linha). */
  protected readonly commonProductsCount = new Set(this.data.items.map(item => item.productId)).size;

  protected readonly otherColumnLabel = this.showPurchaseColumn
    ? 'Na outra solicitação'
    : `Na #${this.otherPurchaseIds[0] ?? '—'}`;

  /** Comparação de cada item repetido com o mesmo produto na solicitação aberta. */
  private readonly comparisons = new Map<string, MergeComparison>(
    this.data.items.map(item => [item.orderId, this.compare(item)])
  );

  protected comparisonOf(row: MergeItem): MergeComparison {
    const base = this.comparisons.get(row.orderId) ?? this.compare(row);
    const branches = this.otherBranches();
    const otherBranch = branches === undefined ? undefined : (branches[row.orderId] ?? '');
    const currentBranch = base.current ? this.branchLabel(base.current) : '';
    return {
      ...base,
      otherBranch,
      diff: {
        ...base.diff,
        branch: !!base.current && otherBranch !== undefined && otherBranch !== currentBranch
      }
    };
  }

  /** Resumo dos fornecedores de um lado: "1 fornecedor · 5 sugestões" ou "Sem fornecedor". */
  protected suppliersLabel(suppliers: PurchaseSupplier[] | null | undefined): string {
    const list = suppliers ?? [];
    if (list.length === 0) return 'Sem fornecedor';
    const pending = list.filter(s => s.linkStatus === PurchaseSupplierLinkStatus.PENDENTE).length;
    const confirmed = list.length - pending;
    const parts: string[] = [];
    if (confirmed) parts.push(`${confirmed} ${confirmed === 1 ? 'fornecedor' : 'fornecedores'}`);
    if (pending) parts.push(`${pending} ${pending === 1 ? 'sugestão' : 'sugestões'}`);
    return parts.join(' · ');
  }

  protected suppliersTooltip(row: MergeItem, suppliers: PurchaseSupplier[] | null | undefined): ItemSuppliersSummary {
    return buildItemSuppliersSummary(row.name, suppliers, false);
  }

  /**
   * Fornecedores da outra solicitação que ainda não estão nesta. Ao unificar os fornecedores são
   * somados; os que já existem nos dois lados contam uma vez só.
   */
  protected newSuppliersCount(row: MergeItem, current?: SendPurchaseRequestForQuoteItem): number {
    const key = (s: PurchaseSupplier) => s.id || s.cnpj || s.email || s.name;
    const existing = new Set((current?.suppliers ?? []).map(key));
    return new Set((row.suppliers ?? []).map(key).filter(k => !existing.has(k))).size;
  }

  /** Código da filial e apelido do endereço (o mesmo rótulo da tabela de itens). */
  protected readonly branchLabel = branchLabel;


  /** Busca título e itens das outras solicitações, para mostrar filial e nome no comparativo. */
  private loadOtherPurchases(): void {
    if (this.otherPurchaseIds.length === 0) {
      this.otherBranches.set({});
      return;
    }

    forkJoin(
      this.otherPurchaseIds.map(id =>
        this.purchasingService.findPurchaseById(id).pipe(
          map(res => res.body),
          catchError(() => of(null)),
          // Os itens são filtrados por status na API: usa o status da própria solicitação.
          switchMap(purchase =>
            this.purchasingService.findAllProductsForQuote(id, purchase?.status?.[0]).pipe(
              map(res => ({ id, purchase, items: res.body ?? [] })),
              catchError(() => of({ id, purchase, items: [] as SendPurchaseRequestForQuoteItem[] }))
            )
          )
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        const titles: Record<string, string> = {};
        const branches: Record<string, string> = {};
        for (const { id, purchase, items } of results) {
          if (purchase?.title) titles[id] = purchase.title;
          for (const item of items) branches[item.orderId] = this.branchLabel(item);
        }
        this.otherTitles.set(titles);
        this.otherBranches.set(branches);
      });
  }

  protected costCenterLabel(item: { costCenter?: string[] | string | null }): string {
    const cc = item.costCenter;
    if (Array.isArray(cc)) return cc.filter(Boolean).join(', ');
    return cc ?? '';
  }

  private compare(item: MergeItem): MergeComparison {
    const current = (this.data.currentItems ?? []).find(
      (c: SendPurchaseRequestForQuoteItem) => c.productId === item.productId
    );
    if (!current) {
      return {
        current: undefined,
        diff: { quantity: false, costCenter: false, requester: false, branch: false }
      };
    }
    return {
      current,
      diff: {
        quantity: current.quantity !== item.quantity || current.unit?.toLowerCase() !== item.unit?.toLowerCase(),
        costCenter: this.costCenterLabel(current) !== this.costCenterLabel(item),
        requester: current.requester?.id !== item.requester?.id,
        branch: false
      }
    };
  }

  protected displayedColumns: string[] = [
    'select',
    ...(this.showPurchaseColumn ? ['purchase'] : []),
    'id',
    'name',
    'current',
    'other'
  ];

  protected readonly itemSuppliersTooltipComponent = ItemSuppliersTooltipComponent;


  ngOnInit(): void {
    this.loadOtherPurchases();
    this.dataSource.set(new MatTableDataSource(this.data.items));
  }

  ngAfterViewInit() {
    this.dataSource().sort = this.sort();
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected unifyRequests(): void {
    const selectedRequests = this.selection.selected;
    if (!selectedRequests.length) return;
    this._dialogService
      .open(
        {
          headerTitle: 'Confirmar Unificação',
          title: 'Deseja unificar estas solicitações de compra?',
          description:
            'Após a unificação, as solicitações originais serão excluídas e substituídas por uma única solicitação consolidada.',
          labelConfirm: 'Unificar',
          labelCancel: 'Ainda não'
        },
        ModalTypes.SUCCESS
      )
      .subscribe(confirmed => {
        if (confirmed === Action.CONFIRM) {
          this.dialogRef.close({ action: 'unify', requests: selectedRequests });
        }
      });
  }

  protected isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource().data.length;
    return numSelected === numRows;
  }

  protected toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource().data);
  }

  protected checkboxLabel(item?: MergeItem): string {
    if (!item) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(item) ? 'deselect' : 'select'} item`;
  }

}
